import { useRef, useState, use } from 'react';
import { resetCaches } from '@apollo/client';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Button, ButtonBase, Card, CardContent, ClickAwayListener, Divider, Grid, InputAdornment, MenuItem, Paper, Popper, Select, Stack, Typography } from '@mui/material';
import Fade from '@mui/material/Fade';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth0 } from '@auth0/auth0-react';
import { LayoutContext } from '@/layout';
import { useTranslation } from 'react-i18next';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { useUpdateUserLanguage } from '@/operations/mutations/updateUserLanguage';
import { useSnackbar } from 'notistack';
import { ta_user_user_language } from '@nemlia/hasura-schema';
import GlobalContext from '@/layout/Providers/GlobalContext';
const Profile = () => {
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    t
  } = useTranslation();
  const {
    user,
    role,
    refetch
  } = use(GlobalContext);
  const {
    isMobile
  } = use(LayoutContext);
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    logout,
    getIdTokenClaims
  } = useAuth0();
  const {
    mutate: updateLanguage
  } = useUpdateUserLanguage({
    id: '',
    user_language: ''
  });
  const handleToggle = () => {
    setOpen(!open);
  };
  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (selectOpen || anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };
  const handleLanguageChange = async (e: ta_user_user_language) => {
    setLoading(true);
    await updateLanguage({
      variables: {
        id: user.id,
        user_language: e
      }
    }).then(async () => {
      await refetch.user();
    }).catch(() => {
      enqueueSnackbar(t('styra.message.error_general'), {
        variant: 'error'
      });
    }).finally(() => {
      setLoading(false);
    });
  };
  const iconBackColorOpen = 'grey.300';
  return <Box sx={{
    flexShrink: 0
  }}>
      <ButtonBase sx={{
      p: 0.5,
      bgcolor: open ? iconBackColorOpen : 'transparent',
      borderRadius: 1,
      '&:hover': {
        bgcolor: 'secondary.lighter'
      }
    }} aria-label="open profile" ref={anchorRef} aria-controls={open ? 'profile-grow' : undefined} aria-haspopup="true" onClick={handleToggle}>
        <Stack direction="row" spacing={1.5} sx={{
        alignItems: 'center'
      }}>
          <Avatar alt="profile user" sx={{
          width: 32,
          height: 32,
          bgcolor: role === 'developer' ? 'error.main' : 'primary.main',
          textTransform: 'capitalize'
        }}>
            {user ? user.full_name?.charAt(0) : ''}
          </Avatar>
          {!isMobile && user ? <Typography variant="subtitle1" sx={{
          fontWeight: 'bold',
          textTransform: 'capitalize'
        }}>
              {user.full_name}
            </Typography> : null}
        </Stack>
      </ButtonBase>
      <Popper placement="bottom-end" open={open} anchorEl={() => anchorRef.current} transition disablePortal popperOptions={{
      modifiers: [{
        name: 'offset',
        options: {
          offset: [0, 9]
        }
      }]
    }}>
        {({
        TransitionProps
      }) => <Fade {...TransitionProps} in={open} timeout={{
        appear: 0,
        enter: 300,
        exit: 150
      }}>
            <Paper sx={{
          width: 290,
          minWidth: 240,
          maxWidth: 290,
          [theme.breakpoints.down('md')]: {
            maxWidth: 250
          }
        }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Card>
                  <CardContent sx={{
                px: 2.5,
                pt: 3
              }}>
                    <Grid container spacing={1.5} sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                      <Grid>
                        <Stack direction="row" spacing={1.25} sx={{
                      alignItems: 'center'
                    }}>
                          <Avatar alt="profile user" sx={{
                        width: 32,
                        height: 32,
                        bgcolor: role === 'developer' ? 'error.main' : 'primary.main'
                      }}>
                            {user ? `${user.full_name?.charAt(0)?.toUpperCase()}` : ''}
                          </Avatar>
                          {user ? <Stack>
                              <Typography variant="h6">{user.full_name}</Typography>
                              <Typography variant="body2" color="textSecondary" sx={{
                          fontWeight: 'bold'
                        }}>
                                {user.email}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {t(`styra.user_roles.${role}`, {
                            defaultValue: role
                          })}
                              </Typography>
                            </Stack> : null}
                        </Stack>
                      </Grid>
                      {user ? <Grid size={12}>
                          <Divider sx={{
                      mb: 2
                    }} />
                          <Select disabled={loading} fullWidth name="user_language" size="small" id="edit_user_language" value={user.user_language} open={selectOpen} onClose={() => setSelectOpen(false)} onOpen={() => setSelectOpen(true)} onChange={e_0 => handleLanguageChange(e_0.target.value as ta_user_user_language)} startAdornment={<InputAdornment position="start">
                                <PublicRoundedIcon />
                              </InputAdornment>}>
                            <MenuItem value="en">{t('settings.languages.en')}</MenuItem>
                            <MenuItem value="de">{t('settings.languages.de')}</MenuItem>
                            <MenuItem value="da">{t('settings.languages.dk')}</MenuItem>
                            <MenuItem value="fo">{t('settings.languages.fo')}</MenuItem>
                          </Select>
                        </Grid> : null}
                      <Grid size={12}>
                        <Button color="error" fullWidth variant="outlined" startIcon={<LogoutRoundedIcon fontSize="large" />} onClick={async () => {
                      // clear app stuff
                      localStorage.clear();
                      sessionStorage.clear();
                      resetCaches();
                      const claims = await getIdTokenClaims();
                      const connection = claims?.['https://nemlia.com/connection'];
                      const isEnterprise = claims?.['https://nemlia.com/is_enterprise'] === true;

                      // Base return
                      const afterAuth0 = `${window.location.origin}/after-auth0-logout`;

                      // If this session came via Keycloak (your alias), do the 2-step hop.
                      const adConnections = ['nemlia-ad-login', 'ad-nemlia-staging'];
                      if (isEnterprise && adConnections.includes(connection)) {
                        logout({
                          logoutParams: {
                            returnTo: afterAuth0,
                            federated: true
                          }
                        });
                      } else {
                        // No upstream IdP (e.g., SMS/email passwordless) — go straight to final page
                        logout({
                          logoutParams: {
                            returnTo: `${window.location.origin}/logged-out`
                          }
                        });
                      }
                    }}>
                          {t('settings.logout')}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </ClickAwayListener>
            </Paper>
          </Fade>}
      </Popper>
    </Box>;
};
export default Profile;